import { useTranslate } from "@made-people/grease-translations";
import { Heading } from "@made-people/blueprint-heading";
import { ButtonAsLink } from "@made-people/blueprint-button";
import { LinkAsLink } from "@made-people/blueprint-link";
import { ResponsiveImage } from "@made-people/blueprint-responsive-image";
import type { Order } from "@made-people/grease-order";
import { getOrderDate, getOrderTotalQuantity, getStatusMessage } from "./utils";
import { ArrowLeft } from "../../../../assets/icons/ArrowLeft";

export const OrderDetails = ({
  order,
  backHref,
  // trackOrderHref,
}: {
  order: Order;
  backHref: string | undefined;
  // trackOrderHref: string | undefined;
}) => {
  const translate = useTranslate();
  const showActions = order.status === "completed";

  const totalQuantity = getOrderTotalQuantity(order);
  const statusMessage = getStatusMessage(order.status, translate);

  return (
    <div className="flex flex-col pb-10">
      {backHref ? (
        <LinkAsLink
          label={
            <span className="group inline-flex items-center gap-5">
              <ArrowLeft className="w-5 transition-transform group-hover:-translate-x-1" />
              {translate("Account_OrderDetails_Title")}
            </span>
          }
          link={{ href: backHref }}
          variants={{ style: "no-underline" }}
          className="hidden lg:block w-fit text-heading-md pb-4 lg:pb-6"
        />
      ) : (
        <Heading
          heading={translate("Account_OrderDetails_Title")}
          headingTag="h2"
          variants={{ headingSize: "md" }}
          className="hidden lg:block pb-4"
        />
      )}

      <Heading
        heading={translate("Account_OrderDetails_Title")}
        headingTag="h2"
        variants={{ headingSize: "sm" }}
        className="pb-4 lg:hidden"
      />

      <div className="border-b border-border-medium py-4">
        <div className="flex flex-col gap-8 xl:grid xl:grid-cols-2 xl:items-end">
          <div className="flex flex-col gap-8 lg:gap-6">
            <span className="text-heading-sm lg:text-heading-sm">
              {statusMessage}
            </span>
          </div>
          {showActions && (
            <div className="lg:flex-row lg:gap-4 lg:justify-end lg:justify-self-end lg:min-w-[400px]">
              <ButtonAsLink
                label={translate("Account_Return_Or_Exchange")}
                variants={{ width: "full", style: "outline" }}
                link={{ href: "returns" }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="border-b border-border-medium py-8">
        <div className="flex justify-between lg:w-[calc(50%-16px)] flex-col gap-3">
          <div className="flex justify-between">
            <span className="text-label-md">
              {translate("Account_OrderDetails_OrderNumber_Label")}
            </span>
            <span className="text-label-md font-medium">
              #{order.orderNumber}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-label-md">
              {translate("Account_OrderDetails_Date")}
            </span>
            <span className="text-label-md font-medium">
              {getOrderDate(order)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-label-md">
              {translate("Account_OrderDetails_PaymentMethod")}
            </span>
            <span className="text-label-md font-medium capitalize">
              {order.paymentMethod}
            </span>
          </div>
        </div>
      </div>

      <div className="border-b border-border-medium py-8">
        <span className="text-label-md font-medium uppercase block pb-4 tracking-wider">
          {translate("Account_OrderDetails_Items", { quantity: totalQuantity })}
        </span>
        <div className="flex flex-col gap-6">
          {order.items.map((item) => {
            const imageSrc = item.product.base.content.media.find(
              (m) => m.type === "image",
            )?.src;
            const dimensions =
              item.type === "product" ? item.itemDimensions : [];

            return (
              <div key={item.lineId} className="flex gap-2 lg:gap-3">
                {imageSrc && (
                  <ResponsiveImage
                    image={{
                      src: imageSrc,
                      alt: item.product.base.content.name,
                    }}
                    loading="lazy"
                    displaySize="113px"
                    className="w-[113px] aspect-product-image object-cover shrink-0"
                  />
                )}
                <div className="flex flex-1 flex-col gap-8 py-2">
                  <div className="flex flex-col gap-4">
                    <span className="text-label-md">
                      {item.product.base.content.name}
                    </span>
                    {item.price.discounted ? (
                      <div className="flex items-end gap-2">
                        <span className="text-label-md font-medium text-sale-price">
                          {item.price.priceEach.formatted}
                        </span>
                        <span className="text-label-md font-medium text-text-less-emphasis-2 line-through">
                          {item.price.priceEachWithoutDiscount.formatted}
                        </span>
                      </div>
                    ) : (
                      <span className="text-body-sm font-medium">
                        {item.price.priceEach.formatted}
                      </span>
                    )}
                  </div>
                  {dimensions.length > 0 && (
                    <div className="flex flex-col gap-4">
                      {dimensions.map((dim) => (
                        <div
                          key={dim.dimensionKey}
                          className="flex items-end gap-0.5 text-label-sm"
                        >
                          <span className="uppercase tracking-wider">
                            {dim.dimensionLabel}
                          </span>
                          <span className="font-medium">{dim.valueLabel}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-2">
        <div className="border-b border-border-medium py-8">
          <div className="flex flex-col gap-3">
            <span className="text-label-md font-medium tracking-wide uppercase">
              {translate("Account_OrderDetails_BillingAddress")}
            </span>
            <span className="text-label-md capitalize">
              {order.billingAddress.firstName} {order.billingAddress.lastName}
            </span>
            <span className="text-label-md capitalize">
              {order.billingAddress.street}
            </span>
            <span className="text-label-md capitalize">
              {order.billingAddress.zipCode}, {order.billingAddress.city},{" "}
              {order.billingAddress.country}
            </span>
            <span className="text-label-md">{order.billingAddress.email}</span>
            <span className="text-label-md capitalize">
              {order.billingAddress.phoneNumber}
            </span>
          </div>
        </div>

        <div className="border-b border-border-medium lg:pl-4 py-8 lg:pr-6">
          <div className="flex flex-col gap-3">
            <span className="text-label-md font-medium tracking-wide uppercase">
              {translate("Account_OrderDetails_ShippingAddress")}
            </span>
            <span className="text-label-md capitalize">
              {order.shippingAddress.firstName} {order.shippingAddress.lastName}
            </span>
            <span className="text-label-md capitalize">
              {order.shippingAddress.street}
            </span>
            <span className="text-label-md capitalize">
              {order.shippingAddress.zipCode}, {order.shippingAddress.city},{" "}
              {order.shippingAddress.country}
            </span>
            <span className="text-label-md">{order.shippingAddress.email}</span>
            <span className="text-label-md capitalize">
              {order.shippingAddress.phoneNumber}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
